import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { listBiddingBlockApprovalRequests } from '@ebalance/proto/bffuser/v1/bidding_block_service-BiddingBlockService_connectquery'
import {
  ListBiddingBlockApprovalRequestsRequestSchema,
  type ListBiddingBlockApprovalRequestsResponse,
} from '@ebalance/proto/bffuser/v1/bidding_block_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { APPROVAL_STATE_REFETCH_INTERVAL_MS } from '~/hooks/bidding/refetchInterval'

/**
 * 受渡日でブロック入札の承認依頼一覧を取得する。
 *
 * fail-safe: ブロック入札はスポット入札ページの 1 タブなので、取得失敗しても
 * error boundary (ErrorLayout = 全画面エラー) に飛ばさない。global QueryClient の
 * `throwOnError: true` を本 query だけ `false` に上書きし、失敗時は data undefined
 * → 呼び出し側がブロックタブ内 Alert で伝える (通常入札タブ・履歴タブの描画は妨げない)。
 *
 * AC-BSP-096: 一覧 response は承認ステータス軸のみで leg 明細を含まないため、 定期
 * 再取得しても編集中の下書きを巻き込まない。 承認者の承認・差戻し (AC-BSP-094) を
 * 実行者の画面へ手動リロードなしで反映するため polling する
 * ([[useListBiddingSpotApprovalRequests]] と同じ理由。 ブロックは主アクション
 * ボタンの出し分け〈JEPX 提出の活性〉も本一覧の approval_state 由来)。
 */
export function useListBiddingBlockApprovalRequests(
  targetDateFilter?: ProtoDate,
  options?: {
    /**
     * false の間は RPC を発火しない (issue #7131 / AC-WFW-006a Decision 1:
     * ワークフローケース対象日が確定するまで対象日依存のデータ取得を開始しない)。
     * 省略時は従来どおり常時有効 (= 後方互換)。
     */
    enabled?: boolean
  },
): UseQueryResult<ListBiddingBlockApprovalRequestsResponse, ConnectError> {
  return useQuery(
    listBiddingBlockApprovalRequests,
    create(ListBiddingBlockApprovalRequestsRequestSchema, {
      targetDateFilter,
    }),
    {
      enabled: options?.enabled,
      refetchInterval: APPROVAL_STATE_REFETCH_INTERVAL_MS,
      throwOnError: false,
    },
  )
}
